import type { EventRecord } from '@/sim/events';
import type { DeitySnapshot } from '@/sim/god';
import type { AnchorSnapshot } from '@/sim/anchor';
import type { ChronicleSnapshot } from '@/sim/chronicle';
import type { CultureSnapshot } from '@/sim/culture';
import type { CarriedSnapshot } from '@/world/carried';
import type { EarthSnapshot } from '@/world/earth';
import type {
  BandState, PathRecord, StructureState, WorkSite,
} from '@/sim/mortals/types';
import type { MortalPressureCell } from '@/sim/mortals/impact';
import type { WildlifeSpeciesId } from '@/sim/wildlife/types';

/**
 * Serialization shapes (spec §49–§51). What survives a save is the sparse
 * history — env deviations, burn scars, live fire, herds, bands, events —
 * never Region or chunk data: the baseline world is a pure function of the
 * seed and rebuilds for free. Persisting it would only invite the copy to
 * disagree.
 *
 * These interfaces mirror each system's internal records field for field, so
 * toSnapshot() is a copy, not a translation — and the copies are complete:
 * decision counters, rng cursors (fire tickCount, event seq, path traversal
 * counts) and per-day bookkeeping all round-trip, because a snapshot that
 * loses a cursor resumes as a *different* world. Everything here is plain
 * JSON-safe data; nothing holds a class or a typed array. Array order is
 * each system's iteration order, and restore re-inserts in array order, so
 * Map insertion order — which is replay order — survives the trip.
 */

/** One EnvField cell: deviations from baseline as of world-time `at`. */
export interface EnvCellSnapshot {
  gx: number; gz: number;
  moisture: number; biomass: number;
  at: number;
  /**
   * How stubborn this cell's deviation is, as a multiple of the base half-life
   * — moisture and biomass separately. Optional, and absent means 1, so a world
   * saved before gods could buy persistence restores as ordinary land and **no
   * `SNAPSHOT_VERSION` bump is owed** — the same trick `carried?`, `earth?` and
   * `chronicle?` play one level up.
   *
   * Note what is NOT here: whose hand it was. `applyEnv` reads this field to
   * tint chunks, so a god id stored on a cell would be a hand that had reached
   * something witnessable (law 10). A number saying *this growth is stubborn*
   * carries no cause.
   */
  pm?: number; pb?: number;
}

export interface EnvFieldSnapshot {
  cells: EnvCellSnapshot[];
}

/**
 * One StrainField cell: which strain this 260 m of ground has been pushed
 * toward, and how far, as of world-time `at`.
 *
 * No array in this record, deliberately: every store here snapshots by spread
 * and a spread copies an array BY REFERENCE, so a flat record makes that
 * silent corruption impossible rather than merely avoided.
 *
 * `v` is the signed tilt × 1000, integer, clamped to ±1000. Quantised on
 * write only; the decayed read is a float. That saves ~10 B a row against
 * float serialisation and removes JSON float-length variance from the
 * snapshot.
 *
 * **It does NOT close `JSON.stringify(Infinity) === null`, and this comment
 * used to say it did.** Quantising on write closes nothing on its own: every
 * write path in `StrainField` quantises a number it got from `valueAt`, so
 * whatever `valueAt` can return, `v` can hold. What closes it is `valueAt`
 * returning a bounded number — NaN included — which is where the guard lives
 * and where `strainField.ts`'s `TILT_SCALE` header carries the autopsy.
 * `tools/strain.ts §1k` prints the null count on every write path, shipped and
 * counterfactual, so the claim is a reading rather than a sentence.
 *
 * Note what is NOT here, for the same reason `EnvCellSnapshot` says it:
 * whose hand it was. `applyStrains` will recolour chunks from this field, so a
 * god id on a cell would be a hand that had reached something witnessable
 * (law 10). A strain KEY carries no cause — it is a fact about which plants
 * these are, which is exactly what a mortal standing in the wood can see.
 */
export interface StrainCellSnapshot {
  gx: number; gz: number;
  k: string;
  v: number;
  at: number;
  /** Held long enough to stop decaying. Never `at: Infinity`. */
  settled?: boolean;
  /** When it settled — the branching gate needs it and it is not derivable. */
  since?: number;
}

export interface StrainFieldSnapshot {
  cells: StrainCellSnapshot[];
}

/** One Overlay scar: severity and birth time; age is derived, never stored. */
export interface ScarCellSnapshot {
  gx: number; gz: number;
  sev: number; at: number;
}

export interface OverlaySnapshot {
  cells: ScarCellSnapshot[];
}

/** One burning FireSim cell, mid-flame. */
export interface FireCellSnapshot {
  gx: number; gz: number;
  fuel: number; ignitedAt: number; intensity: number;
}

/**
 * A fire mid-burn. `last` and `tickCount` must round-trip exactly: the
 * fire's randomness is hashed from tickCount, so a snapshot that loses it
 * resumes as a *different* fire.
 */
export interface FireSnapshot {
  cells: FireCellSnapshot[];
  last: number | null;
  tickCount: number;
  /**
   * The burn in progress — what has scarred out so far, so a save taken
   * mid-fire remembers what it was burning. Optional: a world saved before
   * burns were remembered restores with nothing alight, which is the truth
   * about it, so no `SNAPSHOT_VERSION` bump is owed.
   */
  burn?: {
    from: number; cells: number;
    sx: number; sz: number;
    /**
     * The bounding box of what has scarred out so far. Nullable because it
     * begins at ±Infinity and `JSON.stringify(Infinity)` is `null` — the
     * type says so rather than pretending otherwise, and `FireSim.restore`
     * puts the infinities back.
     */
    x0: number | null; z0: number | null; x1: number | null; z1: number | null;
    lit?: string[];
  };
}

/**
 * The event log and its id cursor. `seq` keeps counting across the save, so
 * ids minted after a restore are the ids live play would have minted.
 */
export interface EventBusSnapshot {
  seq: number;
  records: EventRecord[];
}

/**
 * One band, complete: the public BandState plus the hourly/daily bookkeeping
 * BandSystem keeps privately — the walk under way, the day's toll so far,
 * the open shortage episode. Losing any of these would fork the replay at
 * the first boundary after the restore.
 */
export interface BandSnapshot extends BandState {
  idHash: number;
  /** Parties carry their own walks, so their traces are replay state too. */
  parties: Array<BandState['parties'][number] & {
    walkKey: string;
    trace: Array<{ x: number; z: number }>;
    movedToday: number;
    foragedToday: boolean;
    gatheredToday: boolean;
  }>;
  nextParty: number;
  migrating: boolean;
  shortageOpen: boolean;
  /** The roster is history: who these people are must survive a reload. */
  people: BandState['people'];
  nextPerson: number;
}

/** ConstructionSystem: what stands, what is rising, and the counters of need. */
export interface ConstructionSnapshot {
  nextId: number;
  structures: StructureState[];
  sites: WorkSite[];
  /** Per-band settlement bookkeeping — settledDays/fullDays drive site opening. */
  counters: Array<{ bandId: string; settledDays: number; fullDays: number }>;
}

/** PathSystem: the worn paths plus the traversal counts still earning one. */
export interface PathSystemSnapshot {
  nextId: number;
  version: number;
  paths: PathRecord[];
  pairs: Array<{ key: string; count: number; pathId?: string }>;
}

/** The whole mortal layer: bands, structures, paths, and the ground's wear. */
export interface BandSystemSnapshot {
  nextId: number;
  bands: BandSnapshot[];
  construction: ConstructionSnapshot;
  paths: PathSystemSnapshot;
  pressure: MortalPressureCell[];
  /** Days each pair of bands has spent in one another's country (§48 fusion). */
  contacts?: Array<{ key: string; days: number }>;
}

/** One wildlife social cell, mirror of WildlifeCellState. */
export interface WildlifeCellSnapshot {
  sx: number; sz: number;
  pop: Record<WildlifeSpeciesId, number>;
  cap: Record<WildlifeSpeciesId, number>;
  prevCap: Record<WildlifeSpeciesId, number>;
  carrion: number;
  updatedAt: number;
}

export interface WildlifeSnapshot {
  cells: WildlifeCellSnapshot[];
}

/**
 * One promoted thing (§4): a place history has made significant, keyed by
 * `placeKey`. `standing` is signed as of `at` and decays from there; `since`
 * and `acts` are the history itself and are stored, never derived.
 */
export interface ThingMarkSnapshot {
  key: string;
  kind: 'plant' | 'rock';
  x: number; z: number;
  standing: number;
  at: number;
  since: number;
  acts: number;
}

export interface ThingStoreSnapshot {
  marks: ThingMarkSnapshot[];
}

/** One cell of recovering ground: where, and when it last burned. */
export interface SereCellSnapshot { gx: number; gz: number; at: number }

export interface SereSnapshot {
  cells: SereCellSnapshot[];
}

/** Bump on any breaking shape change; decodeSnapshot refuses mismatches. */
export const SNAPSHOT_VERSION = 16;   // …and the ground grows varieties

/**
 * The whole save. `seed` rebuilds the baseline; `worldTime` restarts the
 * clock; the optional slots restore whichever systems were running. A slot
 * left undefined means "that system starts fresh", not "that system is off".
 */
export interface SimulationSnapshot {
  version: number;
  seed: number;
  worldTime: number;
  env?: EnvFieldSnapshot;
  overlay?: OverlaySnapshot;
  /** Ordinary things history has made important, and how important. */
  things?: ThingStoreSnapshot;
  /**
   * Objects a god physically moved (Design IV §21). Optional on purpose and
   * deliberately *not* a version bump: a world saved before Grasp existed has
   * moved nothing, so its absence is the truth rather than a lost field.
   */
  carried?: CarriedSnapshot;
  /** Earth a god moved. Optional: an old save simply restores flat. */
  earth?: EarthSnapshot;
  /**
   * Ground whose plant mixture has been pushed off its baseline
   * (`world/strainField.ts`). Optional and deliberately **not** a version bump,
   * on this codebase's own precedent (`carried?`, `earth?`, `chronicle?`,
   * `pm`/`pb`): a world saved before strains existed has no tilt anywhere, and
   * that absence is the truth about it rather than a field that went missing.
   *
   * **The bump this feature owes is a different one and it is owed by
   * BEHAVIOUR, not by this field.** The moment `habitat.build` reads plant
   * traits and `capacity()` divides forage, every carrying capacity on the
   * island changes and `public/prehistory.json`'s century becomes a state
   * consistent with rules that no longer exist — a file that would decode
   * perfectly and describe a different island.
   *
   * **The bump this paragraph promised has been paid**: `SNAPSHOT_VERSION`
   * 16 is the wake (2026-08-19) — `habitat.build` reads plant traits, the
   * fire reads and writes the field, the herds' worth, wariness and die-down
   * read animal traits, and a channelled Strengthen presses it. Every
   * carrying capacity on the island changed the moment those readers landed,
   * exactly as this comment predicted when it reserved the bump, and the
   * prehistory was regenerated in the same sitting (run `tools/census.ts`,
   * never quote the old figures). The field itself stays optional on the
   * `carried?` precedent: a save with no tilt anywhere has honestly pressed
   * nothing.
   */
  strains?: StrainFieldSnapshot;
  /**
   * The durable record of divine deeds and the world's durable changes
   * (`sim/chronicle.ts`) — `D §6.2`'s history qualification reads this, not
   * the bounded `events` ring beside it. Optional and deliberately **not** a
   * version bump: a world saved before anything could remember remembers
   * nothing, so an empty journal is the truth about it rather than a field
   * that went missing.
   */
  chronicle?: ChronicleSnapshot;
  fire?: FireSnapshot;
  /** Ground recovering from its last burn — succession's whole state. */
  sere?: SereSnapshot;
  bands?: BandSystemSnapshot;
  wildlife?: WildlifeSnapshot;
  events?: EventBusSnapshot;
  /** Who was acting on this world, the player among them. */
  pantheon?: DeitySnapshot[];
  /** Places gods bound themselves to, and what mortals raised over them. */
  anchors?: AnchorSnapshot;
  /** Which peoples exist, and what each of them has drifted into. */
  cultures?: CultureSnapshot;
}

/** A fresh save: just the seed and the hour. Everything else is baseline. */
export function emptySnapshot(seed: number, worldTime: number): SimulationSnapshot {
  return { version: SNAPSHOT_VERSION, seed, worldTime };
}

/** Plain JSON: every shape above is already wire-safe by construction. */
export function encodeSnapshot(s: SimulationSnapshot): string {
  return JSON.stringify(s);
}

/** Parse and gate on version — refuse to guess at a future format. */
export function decodeSnapshot(text: string): SimulationSnapshot {
  const s = JSON.parse(text) as SimulationSnapshot;
  if (s.version !== SNAPSHOT_VERSION) {
    throw new Error(`snapshot version ${s.version}, expected ${SNAPSHOT_VERSION}`);
  }
  return s;
}

/** localStorage where the platform offers one; nowhere else pretends to. */
const storage = (): Storage | null => {
  const g = globalThis as { localStorage?: Storage };
  return g.localStorage ?? null;
};

/**
 * Save to localStorage under `key`. Returns false — never throws — where no
 * localStorage exists (node, workers) or the write is refused (quota,
 * private mode). Callers own the reaction to an unsaved world.
 */
export function saveLocal(key: string, s: SimulationSnapshot): boolean {
  try {
    const store = storage();
    if (!store) return false;
    store.setItem(key, encodeSnapshot(s));
    return true;
  } catch {
    return false;
  }
}

/**
 * Load from localStorage under `key`. Null — never a throw — where no
 * localStorage exists, nothing was saved, or the saved text is unreadable
 * or the wrong version: a corrupt save reads as no save.
 */
export function loadLocal(key: string): SimulationSnapshot | null {
  try {
    const text = storage()?.getItem(key);
    if (!text) return null;
    return decodeSnapshot(text);
  } catch {
    return null;
  }
}
